import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Dossier,
  getGlassNameDefaultKey,
  getGlassNameGender,
  SinisterGlass,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../button/button.component';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'sc-operation-type-info-sheet',
  styleUrls: ['./operation-type-info-sheet.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: ` <div class="container" data-testid="operationTypeInfoSheet">
    <div class="close-icon-container">
      <button (click)="onClose()">
        <sc-icon name="close"></sc-icon>
        <span class="sr_only">{{
          'GlobalApplicativeConstants.accessibility.closeDialog'
            | translate
              : {
                  dialogTitle: 'OperationTypeInfoSheet.dialogTitle' | translate
                }
        }}</span>
      </button>
    </div>

    <h1 mat-dialog-title id="operation-type-info-title-id" class="sr_only">
      {{ 'OperationTypeInfoSheet.dialogTitle' | translate }}
    </h1>

    <div class="big-icon-container">
      <span class="icon-back-circle">
        <sc-icon name="info"></sc-icon>
      </span>
    </div>
    <div
      class="main-infos"
      *ngIf="!!dossier?.claim?.glass && isReparation !== null"
    >
      <p *ngIf="isReparation === false">
        {{
          'OperationTypeInfoSheet.operationType.replacement'
            | translate: { glassName: glassName, gender: glassNameGender }
        }}
      </p>
      <p *ngIf="isReparation === true">
        {{
          'OperationTypeInfoSheet.operationType.repair'
            | translate: { glassName: glassName, gender: glassNameGender }
        }}
      </p>
      <div
        class="additional-glass-container"
        *ngIf="!!dossier?.custom?.additionalGlasses?.length"
      >
        {{ ('OperationTypeInfoSheet.otherGlasses' | translate) + '(' }}
        <span
          *ngFor="
            let glassTranslation of getAdditionalSinisterText() | async;
            last as isLast
          "
        >
          {{
            isLast
              ? ' + ' + glassTranslation + ')'
              : ' + ' + glassTranslation + ','
          }}
        </span>
      </div>
      <p>{{ 'OperationTypeInfoSheet.ruleInfo' | translate }}</p>
    </div>
    <sc-button (click)="onClose()" [attr.data-testid]="'nextButton'">{{
      'OperationTypeInfoSheet.buttonLabel' | translate
    }}</sc-button>
  </div>`,
  imports: [
    IconComponent,
    NgIf,
    NgFor,
    ButtonComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class OperationTypeInfoSheetComponent implements OnInit {
  @Input() dossier: Dossier;
  @Output() closeEvent = new EventEmitter<void>();

  glassName: string;
  glassNameGender: string;
  insurerName: string;
  isReparation: boolean = null;
  constructor(
    private readonly configurationService: ConfigurationService,
    private readonly translator: TranslatorService,
    private readonly featureFlippingManager: FeatureFlippingManagerService
  ) {}

  ngOnInit() {
    this.isReparation =
      this.dossier?.claim?.operationType !== null
        ? this.dossier?.claim?.operationType === 'Repair'
        : null;
    this.insurerName = this.configurationService?.getConfig()?.insurerName;
    this.glassName = this.translator
      .getInstantTranslation(getGlassNameDefaultKey(this.dossier?.claim?.glass))
      .toLowerCase();
    this.glassNameGender = getGlassNameGender(this.dossier?.claim?.glass);
  }

  getAdditionalSinisterText() {
    const usedGlasses: SinisterGlass[] = this.dossier.custom?.additionalGlasses;

    return forkJoin(
      usedGlasses
        .filter(
          (glass, i) =>
            usedGlasses.findIndex(
              (sameGlass, j) =>
                sameGlass.glass === glass.glass &&
                ((!sameGlass.glassExtended && !glass.glassExtended) ||
                  sameGlass.glassExtended === glass.glassExtended) &&
                j < i
            ) === -1 &&
            (glass.glass !== this.dossier?.claim?.glass?.glass ||
              !(
                (!glass.glassExtended &&
                  !this.dossier?.claim?.glass?.glassExtended) ||
                glass.glassExtended === this.dossier.claim.glass.glassExtended
              ))
        )
        .map((glass) =>
          this.translator
            .getTranslation(getGlassNameDefaultKey(glass))
            .pipe(take(1))
        )
    );
  }

  onClose() {
    this.closeEvent.emit();
  }
}
